import { useCallback, useState } from 'react';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { formatBytes } from '../../../../lib/formatBytes';
import { EvidenceFileTypeModal } from '../components/EvidenceFileTypeModal';
import { evidenceFileTypesApi } from '../evidenceApi';
import type { EvidenceFileTypeItem } from '../evidenceTypes';

/**
 * Which file types are accepted as evidence, and how large each may be — "configurable per type".
 *
 * <b>Two departures from the index contract, both because the rows are the CODE's.</b> The list is
 * the closed set of types the server can verify by magic bytes — nine — so it is fetched WHOLE
 * and searched and sorted here, the way a section that arrives whole is; and nothing is created
 * or deleted, so there is no Create, no delete and no status facet. The anchor opens the EDIT
 * rather than a profile: a row is two settings, and a page for them would restate the row.
 */
export function EvidenceFileTypesPage() {
  const toast = useToast();
  const [editing, setEditing] = useState<EvidenceFileTypeItem | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const fetchTypes = useCallback(async (query: IndexQueryState, signal: AbortSignal) => {
    const all = await evidenceFileTypesApi.list(signal);
    const term = query.search?.trim().toLowerCase() ?? '';

    const items = term
      ? all.filter(
          (t) =>
            t.name.toLowerCase().includes(term) ||
            t.contentType.toLowerCase().includes(term) ||
            t.extensions.some((e) => e.includes(term)),
        )
      : all;

    return { items, total: items.length, page: 1, pageSize: items.length || 1, facets: {} };
  }, []);

  return (
    <>
      <IndexPage<EvidenceFileTypeItem>
        module="evidence-file-types"
        breadcrumb={['Evidence Management', { label: 'Settings', to: '/evidence/settings' }, 'File Types']}
        columns={COLUMNS}
        facets={[]}
        auditEntityTypes={['EvidenceFileType']}
        rowKey={(type) => type.id}
        fetch={fetchTypes}
        reloadKey={reloadKey}
        searchPlaceholder="Search file type or extension"
        emptyMessage="No evidence file types are configured."
        onRowOpen={(type) => setEditing(type)}
        openPermission="evidence-file-types.update"
        // A seeded configuration row nobody CREATED — no provenance, so no audit columns. The
        // last change is in History Logs.
        otherInfo={() => null}
        auditColumns={false}
      />

      {editing ? (
        <EvidenceFileTypeModal
          fileType={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast.success(`${editing.name} was saved.`);
            setEditing(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<EvidenceFileTypeItem>[] = [
  { key: 'name', header: 'File type', width: 220, flex: true, render: (type) => <TextCell value={type.name} /> },
  { key: 'extensions', header: 'Extensions', width: 160, summary: true, render: (type) => <TextCell value={type.extensions.join(', ')} mono /> },
  { key: 'contentType', header: 'Media type', width: 320, render: (type) => <TextCell value={type.contentType} mono /> },
  {
    key: 'maxSizeBytes',
    header: 'Size limit',
    width: 130,
    align: 'right',
    summary: true,
    render: (type) => <TextCell value={formatBytes(type.maxSizeBytes)} mono />,
  },
  {
    key: 'status',
    header: 'Accepted',
    width: 130,
    status: true,
    render: (type) =>
      type.isEnabled ? <StatusText tone="active">Accepted</StatusText> : <StatusText tone="inactive">Not accepted</StatusText>,
  },
];
